import { Group, Menu, OverflowList, Text, UnstyledButton } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { MinimalDriveItem } from "@godalming/api-types";
import { useEffect, useState } from "react";
import { useParentDocumentsQuery } from "../queries";
import { useHeaderAppearanceStore } from "@stores/useHeaderAppearanceStore";

function getBreadcrumbs(root: MinimalDriveItem, targetId: string): Array<{ id: string; name: string }> {
    const path: MinimalDriveItem[] = []

    function dfs(node: MinimalDriveItem): boolean {
        path.push(node)

        if (node.id === targetId) {
        return true
        }

        if (node.children) {
        for (const child of node.children) {
            if (dfs(child)) {
            return true
            }
        }
        }

        path.pop()
        return false
    }

    const found = dfs(root)
    if (!found) return []

    return path.slice(1, path.length).map(item => ({ id: item.id, name: item.name }))
}

export const DocumentExplorerBreadcrumbs: React.FC<{ folderId: string | undefined }> = ({ folderId }) => {

    const { data: documents } = useParentDocumentsQuery()
    const useColouredHeader = useHeaderAppearanceStore((state) => state.useColouredHeader)
    const currentColour = useColouredHeader ? 'white' : 'dimmed'
    const linkColour = useColouredHeader ? 'rgba(255,255,255,0.85)' : 'dimmed'
    const separatorColour = useColouredHeader ? 'rgba(255,255,255,0.75)' : 'var(--mantine-color-dimmed)'

    const [crumbs, setCrumbs] = useState<Array<{ id: string; name: string }>>([])

    useEffect(() => {
        if (!documents || !folderId) {
            setCrumbs([]);
            return;
        }
        const crumbs = getBreadcrumbs(documents, folderId)
        crumbs.unshift({ id: 'documents', name: 'Documents' });
        setCrumbs(crumbs)
    }, [documents, folderId])

    const crumbTarget = (crumb: { id: string }) => crumb.id === 'documents' ? '/documents' : `/documents/${crumb.id}`

    return (
        <OverflowList
            data={crumbs}
            collapseFrom="start"
            gap={0}
            getItemKey={(crumb) => crumb.id}
            renderItem={(crumb, index) => {
                const isLast = index === crumbs.length - 1
                return (
                    <Group key={crumb.id} gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                        {index > 0 ? <Text c={separatorColour} size="sm" lh={1} ml="xs">/</Text> : null}
                        {isLast ? (
                            <Text c={currentColour} fw={600} size="sm" lh={1} aria-current="page">{crumb.name}</Text>
                        ) : (
                            <Link to={crumbTarget(crumb) as any}>
                                <Text c={linkColour} fw={500} size="sm" lh={1}>{crumb.name}</Text>
                            </Link>
                        )}
                    </Group>
                )
            }}
            renderOverflow={(hiddenCrumbs) => (
                <Menu position="bottom-start" withinPortal shadow="sm">
                    <Menu.Target>
                        <UnstyledButton aria-label={`${hiddenCrumbs.length} hidden folder${hiddenCrumbs.length === 1 ? '' : 's'}`}>
                            <Text c={linkColour} fw={600} size="sm" lh={1}>…</Text>
                        </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                        {hiddenCrumbs.map((crumb) => (
                            <Menu.Item key={crumb.id} component={Link} to={crumbTarget(crumb) as any}>
                                {crumb.name}
                            </Menu.Item>
                        ))}
                    </Menu.Dropdown>
                </Menu>
            )}
            w="100%"
            maw="100%"
            style={{ minWidth: 0, alignItems: 'center' }}
        />
    )
}
